import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "@/i18n/translations";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_MEDIA = [
  {
    id: "fallback-1",
    type: "image",
    url: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL0Q1VXFzWG83ZnR6TWE3ZGc2TG9DLmpwZyJ9:adyen:EvUNprVyhPrmx12zzMniSLhHxnl7m5qcQtn13On7KsM?format=webp&width=1920&height=1080",
    title: "Paiement sans contact"
  },
  {
    id: "fallback-2",
    type: "image",
    url: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL010SlJqSEpEdHpNYjZwRE5IYWdELmpwZyJ9:adyen:lnQNwG_ZRBS9c7IxxqA-L1Wlw4CupLLWDELfSuZDErc?format=webp&width=1920&height=1080",
    title: "Terminal de paiement en boutique"
  },
  {
    id: "fallback-3",
    type: "image",
    url: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL1hiTDVYRWMzZEdQVXRYam9tSlBtLmpwZyJ9:adyen:svEhNvY1Fosxk_g2krrkqa2O5_2wRMaL0-SsVJFaZzQ?format=webp&width=1920&height=1080",
    title: "Tap to Pay en commerce"
  }
];

export default function HeroCarousel() {
  const [media, setMedia] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const res = await axios.get(`${API}/media/active`);
        if (res.data && res.data.length > 0) {
          const processed = res.data.map(item => ({
            ...item,
            url: item.url.startsWith("http") ? item.url : `${API.replace('/api', '')}${item.url}`
          }));
          setMedia(processed);
        } else {
          setMedia(FALLBACK_MEDIA);
        }
      } catch {
        setMedia(FALLBACK_MEDIA);
      }
      setLoaded(true);
    };
    fetchMedia();
  }, []);

  const next = useCallback(() => {
    setCurrent(c => (c + 1) % media.length);
  }, [media.length]);

  const prev = useCallback(() => {
    setCurrent(c => (c - 1 + media.length) % media.length);
  }, [media.length]);

  // Auto-advance
  useEffect(() => {
    if (media.length <= 1) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [media.length, next]);

  if (!loaded) return <div className="h-screen bg-[#0A1128]" />;

  const currentMedia = media[current];

  return (
    <div data-testid="hero-carousel" className="relative h-[100svh] w-full overflow-hidden">
      {/* Media backgrounds */}
      {media.map((item, idx) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${idx === current ? "opacity-100" : "opacity-0"}`}
        >
          {item.type === "video" ? (
            <video
              src={item.url}
              autoPlay
              loop
              muted
              playsInline
              className="hero-video"
            />
          ) : (
            <img
              src={item.url}
              alt={item.title || "AyaPay"}
              className="w-full h-full object-cover"
            />
          )}
        </div>
      ))}

      {/* Overlay */}
      <div className="hero-overlay absolute inset-0" />

      <div className="relative z-10 flex flex-col justify-center h-full max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-0">
        <div className="max-w-2xl">
          <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-[#00C853] mb-3 md:mb-4">
            {t("hero.tag")}
          </span>
          <h1
            data-testid="hero-title"
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-4 md:mb-6"
          >
            {t("hero.title1")}
            <br />
            <span className="text-[#00C853]">{t("hero.title2")}</span>
            <br />
            {t("hero.title3")}
          </h1>
          <p className="text-sm md:text-base lg:text-lg text-slate-200 leading-relaxed mb-6 md:mb-8 max-w-lg">
            {t("hero.desc")}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="/contact"
              data-testid="hero-cta-demo"
              className="cta-pulse inline-flex items-center justify-center font-semibold bg-[#00C853] text-white rounded-full px-7 py-3 text-sm hover:bg-[#00A844] transition-all duration-200"
            >
              {t("hero.cta1")}
            </a>
            <a
              href="/tarifs"
              data-testid="hero-cta-solutions"
              className="inline-flex items-center justify-center font-semibold border-2 border-white/30 text-white rounded-full px-7 py-3 text-sm hover:bg-white/10 transition-all duration-200"
            >
              {t("hero.cta2")}
            </a>
          </div>
        </div>
      </div>

      {/* Carousel controls */}
      {media.length > 1 && (
        <>
          <button
            data-testid="hero-prev"
            onClick={prev}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 items-center justify-center text-white hover:bg-white/20 transition-all hidden md:flex"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            data-testid="hero-next"
            onClick={next}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 items-center justify-center text-white hover:bg-white/20 transition-all hidden md:flex"
          >
            <ChevronRight size={18} />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {media.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrent(idx)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  idx === current ? "bg-[#00C853] w-6" : "bg-white/40"
                }`}
              />
            ))}
          </div>
        </>
      )}

      {/* Bottom badges */}
      <div className="absolute bottom-8 right-8 z-20 hidden lg:flex items-center gap-4">
        <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/10">
          <span className="text-xs text-white/70">PCI-DSS</span>
        </div>
        <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/10">
          <span className="text-xs text-white/70">Swiss Made</span>
        </div>
      </div>
    </div>
  );
}
